import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Icon } from '../Icon';
import { IconMap } from '../IconMap';
import { SocialLink } from '@workspace/interfaces';
import { cn } from '@workspace/lib';
import { theme } from '@workspace/ui/theme';

export interface SocialLinksProps {
  links: SocialLink[];
  className?: string;
  spacing?: number; // Spacing between icons in pixels
  spacingMode?: 'margin' | 'gap';
}

function SocialIcon({
  platform,
  // color = 'light',
  icon,
  iconUrl,
  size = 20,
}: {
  platform: string;
  icon?: { _ref: string; url: string };
  iconUrl?: string;
  size?: number;
}) {
  const imageUrl = icon?.url || iconUrl;
  const iconColor = theme === 'redRec' ? 'text-white' : 'text-black';
  if (imageUrl) {
    return <Image src={imageUrl} alt={`${platform} icon`} width={size} height={size} className={iconColor} />;
  }

  return (
    <div className="icon-container">
      <Icon name={platform as keyof typeof IconMap} size={size} className={iconColor} />
    </div>
  );
}

export function SocialLinks({ links, className = '', spacing = 20, spacingMode = 'margin' }: SocialLinksProps) {
  const spacingClass = spacingMode === 'gap' ? '' : spacing === 16 ? 'mr-4 last:mr-0' : 'mr-4 last:mr-0';

  return (
    <div className={cn('flex', className)}>
      {links?.map((social, index) => {
        const icon = (
          <SocialIcon
            platform={social.platform}
            // color={social.color}
            icon={social.icon}
            iconUrl={social.iconUrl}
            size={24}
          />
        );

        const anchor = social.anchor?.trim() || social.link?.anchor?.trim();

        if (anchor) {
          const href = anchor.startsWith('#') ? anchor : `#${anchor}`;
          return (
            <a
              key={index}
              href={href}
              className={cn('hover:opacity-80 transition-opacity', spacingClass)}
              aria-label={`Scroll to ${social.platform} section`}
            >
              {icon}
            </a>
          );
        }

        const linkUrl = social.link?.url ?? social.url;
        const linkType = social.link?.type ?? social.linkType;
        const openInNewTab =
          linkType === 'external' ? true : (social.link?.isOpenInNewTab ?? social.isOpenInNewTab ?? false);

        if (linkUrl && linkUrl !== '#') {
          if (linkType === 'external') {
            return (
              <a
                key={index}
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn('hover:opacity-80 transition-opacity', spacingClass)}
                aria-label={`Visit our ${social.platform} page`}
              >
                {icon}
              </a>
            );
          }

          if (linkType === 'internal') {
            return (
              <Link
                key={index}
                href={linkUrl}
                target={openInNewTab ? '_blank' : '_self'}
                rel={openInNewTab ? 'noopener noreferrer' : undefined}
                className={cn('hover:opacity-80 transition-opacity', spacingClass)}
                aria-label={`Visit our ${social.platform} page`}
              >
                {icon}
              </Link>
            );
          }

          return (
            <a
              key={index}
              href={linkUrl}
              className={cn('hover:opacity-80 transition-opacity', spacingClass)}
              aria-label={`Visit our ${social.platform} page`}
            >
              {icon}
            </a>
          );
        }

        // No link, just icon
        return (
          <div key={index} className={spacingClass}>
            {icon}
          </div>
        );
      })}
    </div>
  );
}
